/**
 * AddNoteDialog Component
 *
 * Dialog for adding or editing retrospective notes with @mention support
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Notes as NotesIcon } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';
import { MentionInput, MentionData, extractMentions } from '../../../../../components/MentionInput';

export const AddNoteDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { orgObjectives, employees, departments, setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, formState, updateFormField, closeNoteDialog } = dialogs;

    const [mentions, setMentions] = useState<MentionData[]>([]);
    const isEditMode = !!formState.editNoteId;

    // Initialize mentions from content when editing
    useEffect(() => {
        if (isEditMode && formState.newNote) {
            const extractedMentions = extractMentions(formState.newNote);
            setMentions(extractedMentions);
        } else {
            setMentions([]);
        }
    }, [isEditMode, dialogState.noteDialog]);

    const handleContentChange = (value: string, newMentions: MentionData[]) => {
        updateFormField('newNote', value);
        setMentions(newMentions);
    };

    const handleSubmit = async () => {
        if (!formState.newNote.trim()) return;

        // Convert mentions to API format
        const mentionsForApi = mentions.map(m => ({ type: m.type, id: m.id }));

        try {
            if (isEditMode && formState.editNoteId) {
                // Update existing note
                await okrApi.updateNote(formState.editNoteId, formState.newNote, mentionsForApi);
                setSuccess('Note updated successfully');
            } else {
                // Add new note - attach to first org objective
                const targetOrgObj = orgObjectives.length > 0 ? orgObjectives[0] : null;
                if (!targetOrgObj?.id) return;
                await okrApi.addNote(targetOrgObj.id, formState.newNote, mentionsForApi);
                setSuccess('Note added successfully');
            }
            closeNoteDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || `Failed to ${isEditMode ? 'update' : 'add'} note`);
        }
    };

    // Format employees for MentionInput
    const formattedEmployees = (employees || []).map((emp: any) => ({
        id: emp.id || emp.emp_id,
        name: emp.name || emp.full_name || 'Unknown',
    }));

    // Format departments for MentionInput
    const formattedDepartments = (departments || []).map((dept: any) => ({
        id: dept.id || dept.dept_id,
        dept_name: dept.dept_name || dept.name || dept.department_name,
        name: dept.dept_name || dept.name || dept.department_name,
    }));

    return (
        <Dialog
            open={dialogState.noteDialog}
            onClose={closeNoteDialog}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <NotesIcon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {isEditMode ? 'Edit Retrospective Note' : 'Add Retrospective Note'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Type @ to mention people or teams
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <MentionInput
                        value={formState.newNote}
                        onChange={handleContentChange}
                        employees={formattedEmployees}
                        departments={formattedDepartments}
                        placeholder="Share your insights, learnings, or observations... Type @ to mention someone"
                        rows={4}
                        isDarkMode={isDarkMode}
                        label="Note"
                        autoFocus
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={closeNoteDialog}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!formState.newNote.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    {isEditMode ? 'Update Note' : 'Add Note'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddNoteDialog;
